import { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import {
  GOOGLE_MAPS_API_KEY,
  isGoogleMapsConfigured,
  PLACES_AUTOCOMPLETE_URL,
  placesDetailsUrl,
} from "@/config/maps";

/**
 * AddressAutocomplete — Places API (New) REST autocomplete.
 * Does not load the legacy Maps JS Autocomplete widget (blocked on new keys).
 * Focused on Switzerland for Byron real estate forms.
 */

const VARIANT_CLASSES = {
  underline:
    "w-full pl-12 pr-4 py-4 border-b border-[#E8E6E1] bg-transparent text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#C6A87C] transition-colors text-lg font-light",
  boxed:
    "w-full pl-12 pr-4 py-4 bg-white border border-[#E5E5E0] text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#8B7355] transition-colors text-sm font-light",
  compact:
    "w-full pl-6 pr-4 py-3 border-b border-[#E5E5E0] text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#8B7355] transition-colors text-sm font-light bg-transparent",
};

const VARIANT_ICON = {
  underline: "absolute left-4 top-4 text-[#999]",
  boxed: "absolute left-4 top-1/2 -translate-y-1/2 text-[#999]",
  compact: "absolute left-0 top-1/2 -translate-y-1/2 text-[#999]",
};

const PRIMARY_TYPES = {
  address: ["street_address", "route", "premise"],
};

const extractLocality = (addressComponents = []) => {
  const preferred = [
    "locality",
    "postal_town",
    "sublocality_level_1",
    "sublocality",
    "administrative_area_level_2",
  ];
  for (const type of preferred) {
    const match = addressComponents.find((c) => c.types?.includes(type));
    if (match?.longText || match?.long_name) {
      return match.longText || match.long_name;
    }
  }
  return null;
};

const fetchSuggestions = async (input, types) => {
  const body = {
    input,
    includedRegionCodes: ["ch"],
    languageCode: "fr",
  };

  // Street-level only for valuation address fields; leave open for city/region search.
  if (Array.isArray(types) && types.includes("address") && !types.includes("geocode")) {
    body.includedPrimaryTypes = PRIMARY_TYPES.address;
  }

  const response = await fetch(PLACES_AUTOCOMPLETE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": GOOGLE_MAPS_API_KEY,
      "X-Goog-FieldMask":
        "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const message = err?.error?.message || `Places autocomplete failed (${response.status})`;
    throw new Error(message);
  }

  const data = await response.json();
  return (data.suggestions || [])
    .map((s) => s.placePrediction)
    .filter(Boolean)
    .map((p) => ({
      placeId: p.placeId,
      text: p.text?.text || "",
      mainText: p.structuredFormat?.mainText?.text || p.text?.text || "",
      secondaryText: p.structuredFormat?.secondaryText?.text || "",
    }));
};

const fetchPlaceLabel = async (placeId, preferLocality) => {
  const response = await fetch(placesDetailsUrl(placeId), {
    method: "GET",
    headers: {
      "X-Goog-Api-Key": GOOGLE_MAPS_API_KEY,
      "X-Goog-FieldMask": "formattedAddress,displayName,addressComponents",
    },
  });

  if (!response.ok) {
    return null;
  }

  const place = await response.json();
  if (preferLocality) {
    const locality = extractLocality(place.addressComponents || []);
    if (locality) return locality;
  }
  return place.formattedAddress || place.displayName?.text || null;
};

const AddressAutocomplete = ({
  value,
  onChange,
  placeholder = "Adresse complète du bien",
  className = "",
  testId = "address-autocomplete",
  variant = "underline",
  types = ["address"],
  iconSize = 18,
  preferLocality = false,
}) => {
  const wrapperRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(isGoogleMapsConfigured);
  const debounceRef = useRef(null);
  const skipNextFetch = useRef(false);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    if (!apiAvailable) return undefined;
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return undefined;
    }

    const query = (value || "").trim();
    if (query.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchSuggestions(query, types);
        setSuggestions(results);
        setOpen(results.length > 0);
      } catch (err) {
        console.warn("Places autocomplete unavailable:", err.message);
        setSuggestions([]);
        setOpen(false);
        // Disable further API calls this session if key/project is not ready
        if (/not been used|disabled|PERMISSION_DENIED|API_KEY|not authorized/i.test(err.message)) {
          setApiAvailable(false);
        }
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => clearTimeout(debounceRef.current);
  }, [value, types, apiAvailable]);

  const handleSelect = async (suggestion) => {
    skipNextFetch.current = true;
    setOpen(false);
    setSuggestions([]);

    let label = suggestion.mainText || suggestion.text;
    try {
      const detailed = await fetchPlaceLabel(suggestion.placeId, preferLocality);
      if (detailed) label = detailed;
      else if (preferLocality && suggestion.mainText) label = suggestion.mainText;
    } catch {
      // keep prediction text
    }

    onChange(label);
  };

  const baseClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.underline;
  const iconClass = VARIANT_ICON[variant] || VARIANT_ICON.underline;

  return (
    <div className="relative" ref={wrapperRef}>
      <MapPin size={iconSize} className={iconClass} strokeWidth={1.5} />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        placeholder={placeholder}
        className={`${baseClass} ${className}`}
        data-testid={testId}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />

      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-auto bg-white border border-[#E5E5E0] shadow-sm text-left"
          role="listbox"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((suggestion) => (
            <li key={suggestion.placeId}>
              <button
                type="button"
                className="w-full text-left px-4 py-3 hover:bg-[#F9F9F7] transition-colors"
                onClick={() => handleSelect(suggestion)}
              >
                <span className="block text-sm text-[#1A1A1A] font-light">
                  {suggestion.mainText}
                </span>
                {suggestion.secondaryText && (
                  <span className="block text-xs text-[#999] font-light mt-0.5">
                    {suggestion.secondaryText}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {loading && (
        <span className="sr-only" aria-live="polite">
          Loading address suggestions
        </span>
      )}
    </div>
  );
};

export default AddressAutocomplete;
